import React, { useCallback, useEffect, useState } from 'react';
import { View, TouchableOpacity, Modal } from 'react-native';
import MyAppText from '../../../components/MyAppText';
import { translate } from '../../../services/translate';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronUp, faTrashCan } from '@fortawesome/free-solid-svg-icons';
import { TextInputMask } from 'react-native-masked-text';
import { ptzPriorityService } from '../../../services/central-api/ptz-priority';
import FormActions from '../../../components/FormActions';
import MyDropDownPicker from '../../../components/MyDropDownPicker';
import { accessService } from '../../../services/central-api/access';
import Toast from 'react-native-toast-message';
import CustomButton from '../../../components/CustomButton';
import { useFocusEffect, useIsFocused } from '@react-navigation/native';
import Container from '../../../components/Container';
import Header from '../../../components/Header';
import Card from '../../../components/Card';
import DataTableContainer from '../../../components/DataTableContainer';
import DataTableHeader from '../../../components/DataTableHeader';
import MyDataTable from '../../../components/MyDataTable';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { Hoverable } from 'react-native-web-hover';

export default function PtzPriority() {
    const { styles, theme } = useStyles(styleSheet);
    const [usersPriorities, setUsersPriorities] = useState<(PtzPriority & { textPriority: string; })[]>([]);
    const [isUserModalVisible, setUserModalVisible] = useState(false);
    const [selectedUser, setSelectedUser] = useState(0);
    const [userDropdownOpen, setUserDropdownOpen] = useState(false);
    const [users, setUsers] = useState<AccessSimplifiedUserData[]>([]);
    const [priority, setPriority] = useState('');
    const [isListDirty, setListDirty] = useState(false);

    async function getPtzPriorities() {
        try {
            const ptzPriorities = await ptzPriorityService.getPtzPriorities();
            setUsersPriorities(ptzPriorities.map(pp => {
                return { ...pp, textPriority: String(pp.priority) };
            }));
        } catch (err) {
            console.error(err);
        }
    }

    async function getUsers() {
        try {
            const users = await accessService.getAccessAllUsers({});
            setUsers(users.filter(user => !usersPriorities.find(up => up.userId == user.id)));
        } catch (err) {
            console.error(err);
        }
    }

    async function savePtzPriorities() {
        try {
            await ptzPriorityService.savePtzPriorities(usersPriorities.map(up => {
                return {
                    userId: up.userId,
                    priority: up.priority
                };
            }));
            setListDirty(false);
            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });
        } catch (err) {
            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }

    const isFocused = useIsFocused();
    useFocusEffect(useCallback(() => {
        if (isFocused) {
            getPtzPriorities();
        }
    }, [isFocused]));

    useEffect(() => {
        getUsers();
    }, [usersPriorities]);


    async function addUser() {
        try {

            await ptzPriorityService.addPtzPriority({ priority: Number(priority), userId: selectedUser });
            await getPtzPriorities();
            setSelectedUser(0);
            setPriority('');
            setUserModalVisible(false);

            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });
        } catch (err) {
            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }

    return (
        <Container>
            <Header
                title={translate('ptzPriority')}
                actions={<CustomButton
                    text={translate('save')}
                    onPress={() => savePtzPriorities()}
                    disabled={!isListDirty}
                />}
            />
            <Card>
                <DataTableHeader
                    title={translate('ptzPriority')}
                    zIndex={2}
                    filter={<CustomButton
                        text={translate('addUser')}
                        onPress={() => setUserModalVisible(true)}
                        type='secondary'
                        disabled={isListDirty}
                    />}
                />
                <DataTableContainer
                    dataTable={
                        <MyDataTable
                            columns={[{
                                name: translate('priority'),
                                button: true,
                                cell: (row, index) => <View style={{ width: 100, paddingVertical: 10, }}>
                                    {index > 0 ?
                                        <TouchableOpacity onPress={() => {
                                            usersPriorities[index].priority = usersPriorities[index].priority - 1;
                                            if (usersPriorities[index].priority == usersPriorities[index - 1].priority) {
                                                usersPriorities[index - 1].priority = usersPriorities[index - 1].priority + 1;
                                            }
                                            usersPriorities[index].textPriority = String(usersPriorities[index].priority);
                                            usersPriorities[index - 1].textPriority = String(usersPriorities[index - 1].priority);
                                            setUsersPriorities([...usersPriorities.sort((a, b) => a.priority - b.priority)]);
                                            setListDirty(true);
                                        }}>
                                            <FontAwesomeIcon color={theme.colors.textColor} icon={faChevronUp} />
                                        </TouchableOpacity>
                                        : <FontAwesomeIcon color={theme.colors.neutral[300]} icon={faChevronUp} />
                                    }
                                    <TextInputMask
                                        value={String(row.textPriority)}
                                        includeRawValueInChangeText={true}
                                        onChangeText={(maskedText, rawText) => {
                                            usersPriorities[index].textPriority = rawText || '';
                                            setUsersPriorities([...usersPriorities]);
                                        }}
                                        type={'only-numbers'}
                                        style={styles.rowInput}
                                        onBlur={() => {
                                            const newPriority = Number(usersPriorities[index].textPriority);
                                            if (!newPriority) {
                                                usersPriorities[index].textPriority = String(usersPriorities[index].priority);
                                                setUsersPriorities([...usersPriorities.sort((a, b) => a.priority - b.priority)]);
                                                return;
                                            }

                                            if (newPriority == usersPriorities[index].priority) {
                                                return;
                                            }

                                            for (const up of usersPriorities) {
                                                // ignore itself
                                                if (up.priority == usersPriorities[index].priority) {
                                                    continue;
                                                }
                                                if (newPriority > usersPriorities[index].priority && up.priority <= newPriority && up.priority > usersPriorities[index].priority) {
                                                    up.priority = up.priority - 1;
                                                    up.textPriority = String(up.priority);
                                                }
                                                if (newPriority < usersPriorities[index].priority && up.priority >= newPriority && up.priority < usersPriorities[index].priority) {
                                                    up.priority = up.priority + 1;
                                                    up.textPriority = String(up.priority);

                                                }
                                            }

                                            usersPriorities[index].priority = newPriority;

                                            setUsersPriorities([...usersPriorities.sort((a, b) => a.priority - b.priority)]);
                                            setListDirty(true);
                                        }}
                                    />
                                    {index != usersPriorities.length - 1 ?
                                        <TouchableOpacity onPress={() => {
                                            usersPriorities[index].priority = usersPriorities[index].priority + 1;
                                            if (usersPriorities[index].priority == usersPriorities[index + 1].priority) {
                                                usersPriorities[index + 1].priority = usersPriorities[index + 1].priority - 1;
                                            }
                                            usersPriorities[index].textPriority = String(usersPriorities[index].priority);
                                            usersPriorities[index + 1].textPriority = String(usersPriorities[index + 1].priority);
                                            setUsersPriorities([...usersPriorities.sort((a, b) => a.priority - b.priority)]);
                                            setListDirty(true);
                                        }}>
                                            <FontAwesomeIcon color={theme.colors.textColor} icon={faChevronDown} />
                                        </TouchableOpacity>
                                        : <FontAwesomeIcon color={theme.colors.neutral[300]} icon={faChevronDown} />
                                    }
                                </View>
                            }, {
                                name: translate('name'),
                                grow: 3,
                                wrap: true,
                                selector: row => row.user.name,
                            }, {
                                name: translate('email'),
                                grow: 2,
                                wrap: true,
                                selector: row => row.user.email,
                            }, {
                                name: translate('actingBody'),
                                width: '200',
                                wrap: true,
                                selector: row => row.user.role?.actingBody?.name ?? '',
                            }, {
                                name: translate('role'),
                                width: '200',
                                wrap: true,
                                selector: row => row.user.role?.name ?? '',
                            }, {
                                name: '',
                                button: true,
                                cell: (row, index) => <Hoverable>
                                    {({ hovered }) => (
                                        <TouchableOpacity
                                            onPress={() => {
                                                usersPriorities.splice(index, 1);
                                                setUsersPriorities([...usersPriorities.sort((a, b) => a.priority - b.priority)]);
                                                setListDirty(true);
                                            }}
                                            style={styles.iconContainer(hovered)}
                                        >
                                            <FontAwesomeIcon color={theme.colors.textColor} icon={faTrashCan} />
                                        </TouchableOpacity>
                                    )}
                                </Hoverable>,
                            }]}
                            data={usersPriorities}
                        />
                    }
                />
            </Card>
            <Modal
                visible={isUserModalVisible}
                animationType='fade'
                onRequestClose={() => {
                    setSelectedUser(0);
                    setPriority('');
                    setUserModalVisible(false);
                }}
                transparent={true}
            >
                <View style={styles.modalContainer}>
                    <View style={styles.modalBody}>
                        <View style={{ rowGap: 5, flex: 1 }}>
                            <MyAppText style={styles.label}>{translate('users')}</MyAppText>
                            <MyDropDownPicker
                                items={users.map(item => {
                                    return {
                                        label: item.name,
                                        value: item.id
                                    };
                                })}
                                searchable={true}
                                open={userDropdownOpen}
                                setOpen={setUserDropdownOpen}
                                setValue={setSelectedUser}
                                value={selectedUser}
                                multiple={false}
                            />
                        </View>
                        <View style={{ rowGap: 5, flex: 1, zIndex: -13 }}>
                            <MyAppText style={styles.label}>{translate('priority')}</MyAppText>
                            <TextInputMask
                                value={priority}
                                includeRawValueInChangeText={true}
                                onChangeText={(maskedText, rawText) => {
                                    setPriority(rawText || '');
                                }}
                                type={'only-numbers'}
                                style={styles.input}
                            />
                        </View>
                        <FormActions
                            onSubmit={addUser}
                            onClose={() => {
                                setSelectedUser(0);
                                setPriority('');
                                setUserModalVisible(false);
                            }}
                            disabled={!selectedUser || !priority} />
                    </View>
                </View>
            </Modal>
        </Container>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    rowInput: {
        color: theme.colors.textColor,
        textAlign: 'center',
    },
    input: {
        color: theme.colors.field.text,
        fontSize: 16,
        fontFamily: 'Open Sans',
        backgroundColor: theme.colors.field.background,
        borderColor: theme.colors.field.border,
        borderWidth: 1,
        padding: 10,
        borderRadius: 4,
    },
    modalContainer: {
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#31313199',
        flex: 1
    },
    modalBody: {
        minWidth: 300,
        backgroundColor: theme.colors.container.background,
        flexDirection: 'column',
        borderRadius: 4,
        padding: 25,
        rowGap: 20
    },
    label: {
        zIndex: -11,
        color: theme.colors.labelColor,
    },
    iconContainer: (hovered: boolean) => ({
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 5,
        height: 25,
        width: 25,
        backgroundColor: hovered ? theme.colors.neutral['100'] : undefined,
    }),
}));
